import { Injectable, Injector } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { filter, concatMap, tap, map, catchError } from 'rxjs/operators';
import { KeycloakProfile } from 'keycloak-js';
import { KeycloakService } from 'keycloak-angular';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { SecureApiService } from 'src/app/services/secure-api.service';
import { environment } from 'src/environments/environment';

/**
 * Combined user state: Keycloak identity + database business profile.
 * Populated once per session by {@link UserConfigService.load}.
 */
export interface UserConfig {
  /** Keycloak user profile (id, username, email, firstName, lastName, etc.) */
  kcProfile: KeycloakProfile;
  /** Roles assigned to the user in Keycloak */
  kcRoles: string[];
  /** User business settings loaded from the database (SecurityGroupMember record) */
  dbSettings: GlobalSettingsData;
}

/**
 * Orchestrates the async loading of all user-specific configuration and caches
 * the result for the lifetime of the session.
 *
 * Responsibilities:
 * 1. Run the initialisation sequence in the correct order:
 *    `isLoggedIn` → `loadUserProfile` (Keycloak) → `postSearchManagerSettings` (DB).
 * 2. Cache the fully-loaded {@link UserConfig} so subsequent calls skip the
 *    network round-trips.
 * 3. Expose {@link getObservableDbSettings} as a drop-in upgrade for
 *    {@link GetglobalsettingsService.getObservableGlobalSettings} — emits only
 *    after the DB profile is guaranteed to be loaded.
 *
 * @remarks
 * {@link SecureApiService} is resolved lazily via `Injector.get()` on the
 * first {@link load} call to break a circular DI dependency:
 *   `GetglobalsettingsService` → `UserConfigService` → `SecureApiService` → `GetglobalsettingsService`
 */
@Injectable({
  providedIn: 'root',
})
export class UserConfigService {

  // ReplaySubject(1): late subscribers always receive the last emitted value
  private _subject$ = new ReplaySubject<UserConfig>(1);

  private _config: UserConfig | null = null;
  private _loaded = false;
  private _loading = false;

  // Lazily resolved on first load() call — avoids circular DI at construction time
  private _secureApi: SecureApiService | null = null;

  /** Observable that emits the full UserConfig once loaded (and replays for late subscribers). */
  readonly userConfig$: Observable<UserConfig> = this._subject$.asObservable();

  constructor(
    private auth: KeycloakService,
    private injector: Injector,
  ) { }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private get secureApi(): SecureApiService {
    if (!this._secureApi) {
      // All constructors have already run by this point, so the circular
      // dependency is fully resolved and Injector.get() is safe to call.
      this._secureApi = this.injector.get(SecureApiService);
    }
    return this._secureApi;
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  /**
   * Triggers the async loading sequence if not already started.
   * Safe to call multiple times — subsequent calls are no-ops.
   *
   * Sequence: isLoggedIn → loadUserProfile (Keycloak) → postSearchManagerSettings (DB)
   *
   * @returns Observable<UserConfig> that emits when loading is complete.
   */
  load(): Observable<UserConfig> {
    if (this._loaded) {
      environment.production || console.log('*** UserConfigService: returning cached config');
      return of(this._config);
    }

    if (this._loading) {
      environment.production || console.log('*** UserConfigService: load already in progress');
      return this.userConfig$;
    }

    this._loading = true;
    environment.production || console.log('*** UserConfigService: starting user config load');

    of(this.auth.isLoggedIn()).pipe(
      filter(logged => {
        if (!logged) {
          console.warn('*** UserConfigService: user is not logged in, skipping load');
        }
        return logged;
      }),
      concatMap(() => this.auth.loadUserProfile(true)),
      concatMap(kcProfile => {
        const kcRoles = this.auth.getUserRoles(true);

        return this.secureApi.postSearchManagerSettings({ sub: kcProfile.id }).pipe(
          filter(settings => settings?.length > 0),
          map(settings => ({
            kcProfile,
            kcRoles,
            dbSettings: Object.assign(new GlobalSettingsData(), settings[0]),
          } as UserConfig)),
        );
      }),
      tap(config => {
        this._config = config;
        this._loaded = true;
        this._loading = false;
        this._subject$.next(config);
        environment.production || console.log('*** UserConfigService: user config fully loaded', config.dbSettings);
      }),
      catchError(err => {
        this._loading = false;
        console.error('*** UserConfigService: failed to load user config', err);
        this._subject$.error(err);
        return of(null as UserConfig);
      }),
    ).subscribe();

    return this.userConfig$;
  }

  // ---------------------------------------------------------------------------
  // Drop-in for GetglobalsettingsService consumers
  // ---------------------------------------------------------------------------

  /**
   * Returns an Observable that emits the user's DB business profile
   * (`GlobalSettingsData`) only after the full load sequence (Keycloak profile
   * + DB settings fetch) has completed.
   *
   * Designed to be called from
   * {@link GetglobalsettingsService.getObservableGlobalSettings} as a
   * transparent upgrade — components keep the same call but now receive a
   * guaranteed-complete DB profile instead of a potentially empty cache.
   */
  getObservableDbSettings(): Observable<GlobalSettingsData> {
    return this.load().pipe(
      map(config => config.dbSettings),
    );
  }

  // ---------------------------------------------------------------------------
  // Cache invalidation
  // ---------------------------------------------------------------------------

  /**
   * Clears the cached config and resets all state flags.
   * Call this on logout alongside `localStorage.clear()`.
   */
  reset(): void {
    this._config = null;
    this._loaded = false;
    this._loading = false;
    this._secureApi = null;
    this._subject$ = new ReplaySubject<UserConfig>(1);
    (this as any).userConfig$ = this._subject$.asObservable();
    environment.production || console.log('*** UserConfigService: cache cleared');
  }

  // ---------------------------------------------------------------------------
  // Synchronous getters (safe to call only after load() has completed)
  // ---------------------------------------------------------------------------

  /** `true` once the full load sequence has completed successfully. */
  get isLoaded(): boolean { return this._loaded; }

  /** Returns the full cached {@link UserConfig}, or `null` before load completes. */
  getConfig(): UserConfig | null { return this._config; }

  /** Returns the Keycloak user profile, or `null` before load completes. */
  getKcProfile(): KeycloakProfile | null { return this._config?.kcProfile ?? null; }

  /** Returns the list of Keycloak realm roles assigned to the user, or `[]` before load completes. */
  getKcRoles(): string[] { return this._config?.kcRoles ?? []; }

  /** Returns the user's DB business profile, or `null` before load completes. */
  getDbSettings(): GlobalSettingsData | null { return this._config?.dbSettings ?? null; }

  /**
   * Returns the user's access group ID from the DB profile.
   * Falls back to `0` before load completes.
   */
  getAccessGroup(): number { return this._config?.dbSettings?.accessGroup ?? 0; }

  /**
   * Returns `true` if the loaded Keycloak roles include the given role name.
   * Always returns `false` before load completes.
   *
   * @param role - Keycloak realm role name to check.
   */
  hasRole(role: string): boolean { return this._config?.kcRoles?.includes(role) ?? false; }
}
